/**
 * Stale deployment chunk recovery (Issue #906).
 *
 * Every Firebase Hosting release replaces the hashed files in `/assets/`. A tab that loaded
 * `index.html` before a deploy still references the old hashes; when it lazily loads a route,
 * the missing JS/CSS chunk fails (the SPA rewrite serves HTML instead), producing errors like
 * `Unable to preload CSS for .../vendor-calendar-<hash>.css`.
 *
 * The correct recovery is a single page reload to fetch the current `index.html` and asset
 * manifest. A sessionStorage timestamp guards against reload loops when an asset is genuinely
 * unavailable, so persistent failures still surface to the ErrorBoundary and system_errors.
 */

export const CHUNK_RELOAD_STORAGE_KEY = 'tas_chunk_reload_at';
export const CHUNK_RELOAD_GUARD_MS = 10000;

const CHUNK_ERROR_PATTERNS = [
  /Unable to preload CSS/i, // Vite __vitePreload CSS failure
  /Failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Loading (CSS )?chunk [\w-]+ failed/i, // Webpack-style / legacy
];

/**
 * Returns true when the error indicates a lazily-loaded JS/CSS chunk could not be fetched.
 * @param {unknown} error
 * @returns {boolean}
 */
export function isChunkLoadError(error) {
  if (!error) return false;
  if (error?.name === 'ChunkLoadError') return true;
  const message = typeof error === 'string' ? error : error?.message;
  if (!message || typeof message !== 'string') return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Performs a single guarded page reload to pick up the latest deployment.
 * Returns true when a reload was triggered; false if a reload was already attempted within
 * the guard window (or storage is unavailable), in which case callers should surface the error.
 * @param {{ storage?: Storage, reload?: () => void, now?: () => number }} [deps]
 * @returns {boolean}
 */
export function reloadForStaleChunk(deps = {}) {
  if (typeof window === 'undefined') return false;
  const storage = deps.storage ?? window.sessionStorage;
  const reload = deps.reload ?? (() => window.location.reload());
  const now = deps.now ?? Date.now;

  try {
    const lastAttempt = Number(storage.getItem(CHUNK_RELOAD_STORAGE_KEY));
    const current = now();
    if (lastAttempt && current - lastAttempt < CHUNK_RELOAD_GUARD_MS) {
      return false;
    }
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(current));
  } catch {
    // Without storage we cannot guard against reload loops; fail safe and surface the error.
    return false;
  }

  reload();
  return true;
}

let recoveryInitialized = false;

/**
 * Registers the Vite `vite:preloadError` listener so stale CSS/JS dependency preloads trigger a
 * single reload instead of throwing into React's error boundary.
 */
export function setupStaleChunkRecovery() {
  if (typeof window === 'undefined' || recoveryInitialized) return;
  recoveryInitialized = true;

  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForStaleChunk()) {
      // Suppress Vite's re-throw; the page is reloading onto the current deployment.
      event.preventDefault();
    }
  });
}

/** Test-only helper to reset module state. */
export function __resetStaleChunkRecoveryForTests() {
  recoveryInitialized = false;
}
